import { useTranslation } from 'react-i18next'
import { Descriptions, Space, Spin, Typography } from 'antd'
import useSWRMutation from 'swr/mutation'
import { moment } from '@BUP/configs/i18n'
import { ComplaintEntriesList } from './entry/list.jsx'
import { UtilsProvider } from '@BUP/contexts/utils-provider.jsx'
import { ComplaintCloseButton } from './close-button.jsx'
import { patchFetcher } from '@BUP/services/Axios/index.js'
import { ComplaintInfractionLevelTag } from '@BUP/components/complaint/infraction-level-tag.jsx'
import { ComplaintStateBadge } from '@BUP/components/complaint/state-badge.jsx'
import TagGroup from './tag-group.jsx'
import SelectForm from './select-form.jsx'
import { useContext } from 'react'
import { UtilsContext } from '@BUP/contexts/utils-context.js'
import styles from './descriptions.module.scss'

export const ComplaintDescriptions = ({ complaint, mutate: mutateGetComplaint, isLoading: isLoadingComplaint }) => {
  const i18n = useTranslation('translation', { keyPrefix: 'common.resources.complaint' })

  const { trigger, isMutating } = useSWRMutation(`/complaints/${complaint.id}`, patchFetcher)
  const handleOnClickCloseButton = async () => {
    await trigger({ state: 'closed' })
    mutateGetComplaint()
  }

  const { data: dataUtils, isLoading: isLoadingUtils } = useContext(UtilsContext)

  if (!dataUtils) return null
  const { complaint: complaintUtils } = dataUtils
  const { protocol_type: protocolTypes, next_steps: nextSteps } = complaintUtils

  return (
    <Space direction='vertical' size='middle'>
      <Descriptions
        column={2}
        layout='vertical'
        labelStyle={{ fontWeight: 'bold' }}
        bordered
        extra={<ComplaintCloseButton loading={isMutating} onClick={handleOnClickCloseButton} state={complaint.state} />}
      >
        <Descriptions.Item span={1} label={i18n.t('attributes.title.label')}>
          <Typography.Text>{complaint.title}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item span={1} label={i18n.t('attributes.state.label')}>
          {isMutating || isLoadingComplaint ? <Spin /> : <ComplaintStateBadge state={complaint.state} />}
        </Descriptions.Item>
        <Descriptions.Item span={2} label={i18n.t('attributes.complainants.label')}>
          <TagGroup elements={complaint.complainants} color='#17bebb' />
        </Descriptions.Item>
        {complaint.infractionType === 'cit007'
          ? (
            <Descriptions.Item span={1} label={i18n.t('attributes.infraction-type.label')}>
              <Typography.Text>{i18n.t(`values.infraction-type.${complaint.infractionType}.label`)}: {complaint.infractionTypeOtherDescription}</Typography.Text>
            </Descriptions.Item>
            )
          : (
            <Descriptions.Item span={1} label={i18n.t('attributes.infraction-type.label')}>
              <Typography.Text>{i18n.t(`values.infraction-type.${complaint.infractionType}.label`)}</Typography.Text>
            </Descriptions.Item>
            )}
        <Descriptions.Item label={i18n.t('attributes.infraction-level.label')}>
          <ComplaintInfractionLevelTag level={complaint.infractionLevel} />
        </Descriptions.Item>
        <Descriptions.Item span={1} label={i18n.t('attributes.involved-people.label')}>
          <TagGroup elements={complaint.involvedPeople} color='#ff8577' />
        </Descriptions.Item>
        <Descriptions.Item span={1} label={i18n.t('attributes.involved-courses.label')}>
          <TagGroup elements={complaint.involvedCourses} color='#6441a5' />
        </Descriptions.Item>
        <Descriptions.Item span={2} label={i18n.t('attributes.description.events_time.label')}>
          <Typography.Paragraph className={styles.description_text}>{complaint.whereAndWhen}</Typography.Paragraph>
        </Descriptions.Item>
        <Descriptions.Item span={2} label={i18n.t('attributes.description.detailed_description.label')}>
          <Typography.Paragraph className={styles.description_text}>{complaint.description}</Typography.Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t('attributes.created-at.label')}>
          <Typography.Text>
            <time dateTime={complaint.createdAt}>{moment(complaint.createdAt).format('LLLL')}</time>
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t('attributes.created-by.label')}>
          <Typography.Text>
            <address>{complaint.creator.user.name}</address>
          </Typography.Text>
        </Descriptions.Item>
        {complaint.state === 'closed' && (
          <>
            <Descriptions.Item label={i18n.t('attributes.closed-at.label')}>
              <Typography.Text>
                {
                    complaint.closedAt
                      ? <time dateTime={complaint.closedAt}>{moment(complaint.closedAt).format('LLLL')}</time>
                      : <time dateTime={complaint.updatedAt}>{moment(complaint.updatedAt).format('LLLL')}</time>
                  }
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label={i18n.t('attributes.closed-by.label')}>
              <Typography.Text>
                {complaint.closer && <address>{complaint.closer.user.name}</address>}
              </Typography.Text>
            </Descriptions.Item>
          </>

        )}
      </Descriptions>
      <section className={styles.text_wrapper}>
        {!complaint.protocolType
          ? (
            <UtilsProvider>
              <SelectForm
                labelText='protocol-type'
                inputName='protocolType'
                complaint={complaint}
                mutateGetComplaint={mutateGetComplaint}
                isLoadingComplaint={isLoadingComplaint}
                options={protocolTypes}
                isLoadingUtils={isLoadingUtils}
              />
            </UtilsProvider>)
          : (
            <p>
              <strong>{i18n.t('attributes.protocol-type.label')}: </strong>{i18n.t(`values.protocol-type.${complaint.protocolType}.label`)}
            </p>
            )}
        {!complaint.nextStep
          ? (
            <UtilsProvider>
              <SelectForm
                labelText='next-steps'
                inputName='nextStep'
                complaint={complaint}
                mutateGetComplaint={mutateGetComplaint}
                isLoadingComplaint={isLoadingComplaint}
                options={nextSteps}
                isLoadingUtils={isLoadingUtils}
              />
            </UtilsProvider>)
          : (
            <p>
              <strong>{i18n.t('attributes.next-steps.label')}: </strong>{i18n.t(`values.next-steps.${complaint.nextStep}.label`)}
            </p>
            )}
      </section>
      <section>
        <Typography.Title level={4}>{i18n.t('attributes.entries.label')}</Typography.Title>
        <Typography.Paragraph type='secondary'>{i18n.t('attributes.entries.description')}</Typography.Paragraph>
        <UtilsProvider>
          <ComplaintEntriesList complaintEntries={complaint.complaintEntries} complaintId={complaint.id} complaintMutate={mutateGetComplaint} bordered={false} />
        </UtilsProvider>
      </section>
    </Space>
  )
}
